/**
 * GetStartedFlow - In-chat multi-step onboarding for solo users
 * Captures role, phase, subject, main goal; then shows recommendations with links.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Icons } from '../../lib/Icons';

const functions = getFunctions(undefined, 'europe-west3');
const updateSoloGetStartedFunc = httpsCallable(functions, 'updateSoloGetStartedFunc');

const ROLES = [
  { id: 'teacher_primary', label: 'Teacher (Primary)' },
  { id: 'teacher_secondary', label: 'Teacher (Secondary)' },
  { id: 'parent', label: 'Parent' },
  { id: 'tutor', label: 'Tutor' },
  { id: 'other_send_educator', label: 'Other SEND educator' },
];

const PHASES = [
  { id: 'eyfs', label: 'EYFS' },
  { id: 'primary', label: 'Primary' },
  { id: 'secondary', label: 'Secondary' },
  { id: 'post16', label: 'Post-16' },
];

const GOALS = [
  { id: 'support_specific_pupil', label: 'Support with a specific pupil' },
  { id: 'classroom_send_friendly', label: 'Make my classroom or school more SEND friendly' },
  { id: 'access_resources', label: 'Access appropriate resources' },
  { id: 'individualised_curriculum', label: 'Individualised curriculum' },
];

function ChoiceButtons({ options, onSelect, saving }) {
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onSelect(opt.id)}
          disabled={saving}
          className="px-4 py-2 rounded-xl bg-slate-800 text-slate-200 hover:bg-violet-600/30 hover:text-white border border-slate-700/50 text-sm transition-colors disabled:opacity-50"
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default function GetStartedFlow({
  onComplete,
  onDismiss,
  addMessage,
  messages,
}) {
  const [step, setStep] = useState('role');
  const [profile, setProfile] = useState({ role: null, phase: null, subject: '', mainGoal: null });
  const [saving, setSaving] = useState(false);

  const saveProfile = async (partial) => {
    setSaving(true);
    try {
      await updateSoloGetStartedFunc({
        soloProfile: { ...profile, ...partial },
      });
      setProfile((p) => ({ ...p, ...partial }));
    } catch (err) {
      console.error('Get-started save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const finishGetStarted = async () => {
    setSaving(true);
    try {
      await updateSoloGetStartedFunc({ getStartedCompleted: true });
    } catch (err) {
      console.error('Get-started complete error:', err);
    } finally {
      setSaving(false);
      onComplete?.();
    }
  };

  const isTeacher = profile.role === 'teacher_primary' || profile.role === 'teacher_secondary';
  const isSecondary = profile.phase === 'secondary' || profile.role === 'teacher_secondary';

  // First article slug by role/phase: primary/EYFS vs secondary
  const firstArticleSlug = isSecondary
    ? 'every-send-friendly-classroom-secondary'
    : 'every-send-friendly-classroom-primary-eyfs';
  const firstArticleTitle = isSecondary
    ? 'Every SEND friendly classroom (Secondary) should have…'
    : 'Every SEND friendly classroom (Primary & EYFS) should have…';

  if (step === 'role') {
    return (
      <div className="space-y-4">
        <ChoiceButtons
          options={ROLES}
          onSelect={async (id) => {
            await saveProfile({ role: id });
            addMessage?.({ role: 'user', content: ROLES.find((r) => r.id === id).label });
            if (id === 'teacher_primary' || id === 'teacher_secondary') {
              setStep('phase');
              addMessage?.({
                role: 'assistant',
                content: "Great. What phase do you mainly work in?",
              });
            } else {
              setStep('goal');
              addMessage?.({
                role: 'assistant',
                content: "Thanks. What are you mainly looking for?",
              });
            }
          }}
          saving={saving}
        />
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="text-xs text-slate-500 hover:text-slate-400 mt-2"
          >
            Skip for now
          </button>
        )}
      </div>
    );
  }

  if (step === 'phase') {
    return (
      <div className="space-y-4">
        <ChoiceButtons
          options={PHASES}
          onSelect={async (id) => {
            await saveProfile({ phase: id });
            addMessage?.({ role: 'user', content: PHASES.find((p) => p.id === id).label });
            if (id === 'secondary') {
              setStep('subject');
              addMessage?.({
                role: 'assistant',
                content: "If you'd like, tell me your main subject (e.g. English, Maths). Or skip.",
              });
            } else {
              setStep('goal');
              addMessage?.({
                role: 'assistant',
                content: "What are you mainly looking for?",
              });
            }
          }}
          saving={saving}
        />
      </div>
    );
  }

  if (step === 'subject') {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            placeholder="e.g. English, Maths"
            value={profile.subject}
            onChange={(e) => setProfile((p) => ({ ...p, subject: e.target.value }))}
            className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 text-sm w-40"
          />
          <button
            type="button"
            onClick={async () => {
              await saveProfile({ subject: profile.subject || undefined });
              addMessage?.({ role: 'user', content: profile.subject || 'Skip' });
              setStep('goal');
              addMessage?.({
                role: 'assistant',
                content: "What are you mainly looking for?",
              });
            }}
            disabled={saving}
            className="px-4 py-2 rounded-xl bg-slate-700 text-slate-200 hover:bg-slate-600 text-sm"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  if (step === 'goal') {
    return (
      <div className="space-y-4">
        <ChoiceButtons
          options={GOALS}
          onSelect={async (id) => {
            await saveProfile({ mainGoal: id });
            addMessage?.({ role: 'user', content: GOALS.find((g) => g.id === id).label });
            setStep('recommendations');
            addMessage?.({
              role: 'assistant',
              content: 'recommendations', // special: render recommendations block
            });
          }}
          saving={saving}
        />
      </div>
    );
  }

  if (step === 'recommendations') {
    return (
      <div className="space-y-4">
        <p className="text-slate-200">
          Thanks, now that I know how to help let's get you started.
        </p>
        <div className="space-y-3 text-sm">
          <p className="text-slate-300">
            <strong className="text-white">1.</strong> Start by reading your first article:{' '}
            <Link
              to={`/solo/articles/${firstArticleSlug}`}
              className="text-violet-400 hover:text-violet-300 underline"
            >
              {firstArticleTitle}
            </Link>
          </p>
          <p className="text-slate-300">
            <strong className="text-white">2.</strong> Make your first student profile so I can
            recommend the right activities for your students or classroom.{' '}
            <Link
              to="/solo/profiles/new"
              className="text-violet-400 hover:text-violet-300 underline"
            >
              Create a learning profile
            </Link>
          </p>
          <p className="text-slate-300">
            <strong className="text-white">3.</strong> Browse activities and resources.{' '}
            <Link to="/solo/resources" className="text-violet-400 hover:text-violet-300 underline">
              Resource Library
            </Link>
          </p>
        </div>
        <button
          type="button"
          onClick={finishGetStarted}
          disabled={saving}
          className="mt-2 px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium disabled:opacity-50"
        >
          Got it
        </button>
      </div>
    );
  }

  return null;
}

export { ROLES, PHASES, GOALS };
